import React, { useEffect, useState } from "react";
import axios from "axios";
import { PhoneCall, Smartphone, Monitor, Loader2, ShieldCheck, AlertCircle, Trash2, ListChecks, Mic, RefreshCw, PhoneOutgoing } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Switch } from "../ui/switch";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { useInAppCall } from "../../contexts/InAppCallContext";

const STATUS_META = {
  ready: { label: "Connected — ready to receive calls", dot: "bg-emerald-500" },
  registering: { label: "Connecting…", dot: "bg-amber-500 animate-pulse" },
  permission_required: { label: "Microphone permission needed", dot: "bg-red-500" },
  error: { label: "Connection error", dot: "bg-red-500" },
  idle: { label: "Not connected", dot: "bg-slate-400" },
};

const MyPhoneStatusPanel = () => {
  const { status, enabled, availability, setAvailability, registerThisDevice, testCall } = useInAppCall();
  const [testing, setTesting] = useState(false);
  if (!enabled) return null;
  const meta = STATUS_META[status] || STATUS_META.idle;

  const handleTestCall = async () => {
    setTesting(true);
    try {
      const res = await testCall();
      toast.success(res?.message || "Test call placed — your device should ring shortly.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not place test call");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="rounded-xl p-4 bg-theme-surface border border-theme space-y-3" data-testid="my-phone-status-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className={`w-2.5 h-2.5 rounded-full ${meta.dot}`} />
          <span className="text-sm font-medium text-foreground" data-testid="my-phone-connection-status">{meta.label}</span>
        </div>
        <div className="flex items-center gap-2">
          {(status === "error" || status === "permission_required" || status === "idle") && (
            <Button variant="outline" size="sm" onClick={registerThisDevice} className="h-7 text-xs" data-testid="my-phone-reconnect-btn">
              <RefreshCw className="w-3 h-3 mr-1" /> Reconnect
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleTestCall} disabled={testing || status !== "ready"} className="h-7 text-xs" data-testid="my-phone-test-call-btn">
            {testing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <PhoneOutgoing className="w-3 h-3 mr-1" />} Test call
          </Button>
        </div>
      </div>
      {status === "permission_required" && (
        <p className="text-xs text-red-500 flex items-center gap-1.5">
          <Mic className="w-3.5 h-3.5" /> Allow microphone access in your browser, then click Reconnect.
        </p>
      )}
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm text-foreground font-medium">My availability</p>
          <p className="text-[11px] text-muted-foreground">Unavailable / Do Not Disturb stops incoming calls ringing on your devices.</p>
        </div>
        <Select value={availability} onValueChange={setAvailability}>
          <SelectTrigger className="w-44 h-8 text-xs" data-testid="my-availability-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="available" data-testid="availability-opt-available">🟢 Available</SelectItem>
            <SelectItem value="unavailable" data-testid="availability-opt-unavailable">🟡 Unavailable</SelectItem>
            <SelectItem value="dnd" data-testid="availability-opt-dnd">🔴 Do Not Disturb</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

/**
 * CallRoutingCard — global view of in-app calling status across the account.
 *
 * Shows the AGGREGATE state across all phone numbers (any number with
 * call_routing_enabled flips this on), the registered Voice-Client devices
 * list, and the account-wide ring_all_devices toggle.
 *
 * Per-number toggles for call_routing_enabled + auto_create_job_from_call
 * live in `PhoneConfigModal` (Configure Call Flow → Call Routing section).
 */
const platformIcon = (p) => (p === "web" ? Monitor : Smartphone);

const CallRoutingCard = ({ API, getAuthConfig }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/phone/call-routing/settings`, getAuthConfig());
      setSettings(res.data);
    } catch {
      toast.error("Failed to load call routing settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSettings(); /* eslint-disable-next-line */ }, []);

  const toggleRingAll = async (next) => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/phone/call-routing/settings`,
        { ring_all_devices: next },
        getAuthConfig(),
      );
      setSettings((s) => ({ ...s, ring_all_devices: res.data.ring_all_devices }));
    } catch {
      toast.error("Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const removeDevice = async (clientId) => {
    try {
      await axios.post(
        `${API}/phone/voice-client/unregister`,
        { client_id: clientId },
        getAuthConfig(),
      );
      toast.success("Device removed");
      fetchSettings();
    } catch {
      toast.error("Failed to remove device");
    }
  };

  const enabledNumbers = (settings?.per_number || []).filter((n) => n.call_routing_enabled);
  const autoLeadNumbers = (settings?.per_number || []).filter((n) => n.auto_create_job_from_call);

  return (
    <Card className="glass-card border-theme mt-4" data-testid="call-routing-card">
      <CardHeader>
        <CardTitle className="text-foreground font-['Manrope'] flex items-center gap-2">
          <PhoneCall className="w-5 h-5" /> Call Routing — In-App Calling
        </CardTitle>
        <CardDescription className="text-muted-foreground">
          Configure in-app calling per phone number under <strong>Configure Call Flow → Call Routing</strong>. This card shows the aggregate status across the account.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <>
            {/* My phone connection + availability */}
            <MyPhoneStatusPanel />

            {/* Per-number summary */}
            <div className="rounded-xl p-4 bg-theme-surface border border-theme">
              <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                <ListChecks className="w-4 h-4 text-emerald-500" />
                Numbers with In-App Calling
                <Badge variant="outline" data-testid="enabled-numbers-count">{enabledNumbers.length} / {(settings?.per_number || []).length}</Badge>
              </h4>
              {(settings?.per_number || []).length === 0 ? (
                <p className="text-xs text-muted-foreground">No phone numbers configured. Buy a Twilio number above to start.</p>
              ) : enabledNumbers.length === 0 ? (
                <p className="text-xs text-muted-foreground">In-app calling is not enabled on any number. Open <strong>Configure Call Flow</strong> on a number above and switch on <strong>Call Routing</strong>.</p>
              ) : (
                <ul className="space-y-1">
                  {enabledNumbers.map((n) => (
                    <li key={n.number_id} className="flex items-center gap-2 text-xs text-foreground">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      <span className="font-mono">{n.phone_number}</span>
                      {n.label && <span className="text-muted-foreground">· {n.label}</span>}
                      {n.auto_create_job_from_call && (
                        <Badge variant="secondary" className="bg-amber-500/15 text-amber-500 border-amber-500/20 h-4 text-[9px]">Lead capture</Badge>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {autoLeadNumbers.length > 0 && (
                <p className="text-[11px] text-muted-foreground mt-2">
                  {autoLeadNumbers.length} number{autoLeadNumbers.length === 1 ? "" : "s"} auto-creating job leads from inbound calls.
                </p>
              )}
            </div>

            {/* Account-wide ring-all toggle (only meaningful when at least one number has routing on) */}
            {enabledNumbers.length > 0 && (
              <div className="flex items-center justify-between p-3 rounded-xl bg-theme-surface/50 border border-theme/50">
                <div className="flex-1 pr-4">
                  <h4 className="text-foreground text-sm font-medium mb-0.5">Ring all my devices</h4>
                  <p className="text-muted-foreground text-xs">When ON, all registered devices ring simultaneously and the first to answer takes the call.</p>
                </div>
                <Switch
                  checked={!!settings?.ring_all_devices}
                  onCheckedChange={toggleRingAll}
                  disabled={saving}
                  data-testid="ring-all-devices-toggle"
                />
              </div>
            )}

            {/* Devices */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-foreground text-sm font-medium flex items-center gap-2">
                  Registered Devices
                  <Badge variant="outline" data-testid="active-clients-count">{settings?.active_client_count || 0}</Badge>
                </h4>
              </div>
              {(settings?.active_clients?.length || 0) === 0 ? (
                <div className="rounded-lg p-4 text-sm text-muted-foreground bg-muted/30 border border-dashed border-border flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-amber-500" />
                  <div>
                    <p className="font-medium text-foreground">No devices registered yet</p>
                    <p className="text-xs mt-0.5">
                      Open this app on your phone or browser. Registration happens automatically when at least one number has Call Routing enabled.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-1.5">
                  {(settings.active_clients || []).map((c) => {
                    const Icon = platformIcon(c.platform);
                    return (
                      <div
                        key={c.client_id}
                        className="flex items-center gap-3 px-3 py-2 rounded-lg bg-muted/40 border border-border/50"
                        data-testid={`device-row-${c.client_id}`}
                      >
                        <div className="w-8 h-8 rounded-full bg-emerald-500/15 flex items-center justify-center">
                          <Icon className="w-4 h-4 text-emerald-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-foreground truncate flex items-center gap-1.5">
                            {c.device_label || "Unnamed device"}
                            {c.live ? (
                              <Badge variant="secondary" className="bg-emerald-500/15 text-emerald-500 border-emerald-500/20 h-4 text-[9px]" data-testid={`device-live-${c.client_id}`}>Live</Badge>
                            ) : (
                              <Badge variant="secondary" className="bg-slate-500/15 text-slate-400 border-slate-500/20 h-4 text-[9px]">Offline</Badge>
                            )}
                          </div>
                          <div className="text-[10px] text-muted-foreground capitalize">
                            {c.platform}{c.member_name ? ` · ${c.member_name}` : ""}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeDevice(c.client_id)}
                          className="h-7 w-7 text-muted-foreground hover:text-red-500"
                          data-testid={`remove-device-${c.client_id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Compliance note */}
            <div className="rounded-lg p-3 text-[11px] flex items-start gap-2 bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-300">
              <ShieldCheck className="w-3.5 h-3.5 shrink-0 mt-px" />
              <p className="leading-relaxed">
                Calls placed from the app use your business Twilio caller ID — customers see your business number.
                On mobile, allow microphone + push notifications so calls ring even when the app is in the background.
              </p>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default CallRoutingCard;
